// PaisSelector.js — Selector de país del visitante (píldora + desplegable)
//
// Se alimenta del catálogo `config_json.paises_config.items` vía PaisContext.
// La bandera sale de `item.bandera` (emoji, URL o ruta subida en el admin) y,
// si no hay ninguna, se arma con el código ISO-2 desde flagcdn.
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { usePais } from '../../contexts/PaisContext';
import { BACKEND_BASE } from '../../services/api';

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  background: ${p => p.$bg || '#fff'};
  border: ${p => p.$bordeAncho || '2px'} solid ${p => p.$borde || '#c084fc'};
  color: ${p => p.$color || '#312e81'};
  border-radius: 999px;
  padding: 0.3rem 0.8rem 0.3rem 0.35rem;
  font-size: ${p => p.$size || '0.95rem'};
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  line-height: 1.2;
  transition: box-shadow 0.2s, border-color 0.2s, color 0.2s;
  &:hover {
    box-shadow: 0 2px 10px rgba(0,0,0,0.12);
    /* Colores de hover opcionales: sin ellos queda solo la sombra de siempre. */
    ${p => p.$bordeHover && `border-color: ${p.$bordeHover};`}
    ${p => p.$colorHover && `color: ${p.$colorHover};`}
  }
  @media (max-width: 600px) {
    font-size: 0.85rem;
    padding: 0.25rem 0.6rem 0.25rem 0.3rem;
  }
`;

const Chevron = styled.span`
  display: inline-block;
  font-size: 0.7em;
  opacity: 0.7;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  transition: transform 0.2s;
`;

const ResetBtn = styled.button`
  width: 28px; height: 28px;
  border-radius: 50%;
  border: none;
  background: ${p => p.$bg || '#ede9fe'};
  color: ${p => p.$color || '#6d28d9'};
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { filter: brightness(0.95); }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 240px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(15,23,42,0.18);
  padding: 0.4rem;
  z-index: 1200;
  max-height: 60vh;
  overflow-y: auto;
`;

const Opcion = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background: ${p => (p.$activo ? (p.$activoBg || '#f5f0ff') : 'transparent')};
  border: none;
  border-radius: 12px;
  padding: 0.55rem 0.7rem;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  color: ${p => (p.$activo ? (p.$acento || '#6d28d9') : '#1e293b')};
  font-weight: ${p => (p.$activo ? 700 : 500)};
  text-align: left;
  &:hover { background: ${p => (p.$activo ? (p.$activoBg || '#f5f0ff') : '#f8fafc')}; }
`;

const Nombre = styled.span`flex: 1; white-space: nowrap;`;

const Sigla = styled.span`
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: ${p => (p.$activo ? (p.$acento || '#a855f7') : '#f1f5f9')};
  color: ${p => (p.$activo ? '#fff' : '#64748b')};
`;

const FlagImg = styled.img`
  width: ${p => p.$size || '30px'};
  height: ${p => p.$size || '30px'};
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #e2e8f0;
`;

const FlagEmoji = styled.span`
  font-size: ${p => p.$size || '1.5rem'};
  line-height: 1;
  flex-shrink: 0;
`;

// Un valor de bandera puede ser: emoji, URL absoluta, o ruta relativa subida
// desde el admin. Sin bandera se cae a flagcdn con el código ISO-2.
const esEmoji = (v) => !!v && !/^(https?:|uploads\/|\/)/i.test(v);

export const Bandera = ({ item, size = '30px' }) => {
  const b = (item?.bandera || '').trim();
  if (b && esEmoji(b)) return <FlagEmoji $size={size}>{b}</FlagEmoji>;
  const iso = (item?.codigo || '').trim().toLowerCase();
  const src = b
    ? (b.startsWith('http') ? b : `${BACKEND_BASE}/${b.replace(/^\//, '')}`)
    : (iso.length === 2 ? `https://flagcdn.com/w80/${iso}.png` : null);
  if (!src) return null;
  return <FlagImg src={src} alt={item?.nombre || ''} $size={size} loading="lazy" />;
};

const PaisSelector = ({
  colorBorde, colorBordeAncho, colorBordeHover, colorTextoHover,
  colorTexto, colorFondo, acento, tamano, mostrarReset = true,
}) => {
  const { paises, paisActual, setPais, resetPais, selectorDisponible, eleccionManual } = usePais();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (!selectorDisponible) return null;

  return (
    <Wrap ref={ref}>
      <Pill
        type="button"
        $bg={colorFondo}
        $borde={colorBorde}
        $bordeAncho={colorBordeAncho}
        $bordeHover={colorBordeHover}
        $colorHover={colorTextoHover}
        $color={colorTexto}
        $size={tamano}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Bandera item={paisActual} size="28px" />
        <span>{paisActual?.nombre || 'País'}</span>
        <Chevron $open={open}>▼</Chevron>
      </Pill>

      {mostrarReset && eleccionManual && (
        <ResetBtn
          type="button"
          title="Restablecer"
          $bg={acento ? `${acento}22` : undefined}
          $color={acento}
          onClick={() => { resetPais(); setOpen(false); }}
        >
          ×
        </ResetBtn>
      )}

      {open && (
        <Menu role="listbox">
          {paises.map(p => {
            const activo = paisActual && (p.codigo || p.nombre) === (paisActual.codigo || paisActual.nombre);
            return (
              <Opcion
                key={p.codigo || p.nombre}
                type="button"
                role="option"
                aria-selected={!!activo}
                $activo={!!activo}
                $acento={acento}
                onClick={() => { setPais(p.codigo || p.nombre); setOpen(false); }}
              >
                <Bandera item={p} size="30px" />
                <Nombre>{p.nombre}</Nombre>
                <Sigla $activo={!!activo} $acento={acento}>
                  {(p.sigla || p.codigo || '').toUpperCase()}
                </Sigla>
                {activo && <span style={{ color: acento || '#a855f7' }}>✓</span>}
              </Opcion>
            );
          })}
        </Menu>
      )}
    </Wrap>
  );
};

export default PaisSelector;
